<template>
  <Loading v-if="loading || !user" />
  <div class="infoUser">
    <div class="infoUser__header">
      <Link :title="$t('backToUsers')" @back-to="navigateBack" />
    </div>
    <Title :title="$t('user')" />
    <div class="infoUser__card">
      <FormKit
        v-if="user"
        id="addUserForm"
        v-model="formData"
        type="form"
        :actions="false"
        @submit="submitHandler"
      >
        <AboutUser
          :user-role-dropdown="userRoleDropdown"
          @select-user-role="selectUserRole"
        />
        <FormKit type="group" name="role">
          <template v-for="(access, index) in accesses" :key="index">
            <Checkbox
              class="infoUser__card-checkbox"
              :name="access.name"
              :value="formData.role[access.name]"
              :title="access.title"
              :data-testid="access.testId"
              @change="changeCheckboxes(access.name)"
            />
          </template>
        </FormKit>
      </FormKit>
      <div v-if="user" class="infoUser__card__lastUpdate-key">
        <div v-if="user.updatedAt" class="content">
          <p class="title">{{ $t('lastUpdate') }}</p>
          <span class="subtitle">{{ dateTime(user?.updatedAt) }}</span>
        </div>
        <div class="content">
          <p class="title">{{ $t('key') }}</p>
          <span class="subtitle">{{ user?.id }}</span>
        </div>
      </div>
    </div>
    <div class="infoUser__changePassword" @click="showModal = true">
      <img src="@/assets/icons/ChangePassword.svg" alt="" />
      <span class="title">{{ $t('changePassword') }}</span>
    </div>
    <Button
      v-if="isUserMain"
      variant="redBordered"
      :title="$t('deleteUser')"
      @click="showModalDeleteUser = true"
    />
  </div>
  <Footer @clean-form="cleanForm" @submit-form="submitForm" />
  <ChangePassword v-if="showModal" @close-modal="showModal = false" />
  <DeleteUser
    :show-modal-delete-user="showModalDeleteUser"
    :user="user"
    @close-modal="showModalDeleteUser = false"
    @delete-user-request="deleteUserRequest"
  />
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import router from '@/router';
import moment from 'moment';

import Loading from '@/views/Loading.vue';
import AboutUser from './AboutUser/AboutUser.vue';
import ChangePassword from './ChangePassword/ChangePassword.vue';
import DeleteUser from './DeleteUser/DeleteUser.vue';

import { defaultFormData, columns, accesses } from './configs';

import { Option } from '@/types';
import { AppRole } from '@/store/modules/roles/types';
import { PharmacyAccesses } from '@/store/modules/account/types';
import { PharmacyAccessesAdd } from '@/store/modules/users/types';

@Options({
  name: 'InfoUser',
  components: {
    Loading,
    AboutUser,
    ChangePassword,
    DeleteUser
  },
  data() {
    return {
      formData: defaultFormData,
      userRoleDropdown: {
        selectedOption: {
          value: '',
          label: ''
        },
        options: []
      },
      pharmacyId: localStorage.getItem('id'),
      accesses: accesses(this.$t),
      columns: columns(this.$t),
      loading: false,
      showModal: false,
      showModalDeleteUser: false
    };
  },
  created() {
    const { id } = this.$route.params;

    if (id) {
      this.fetchUserAndRoles();
    }
  },
  unmounted() {
    this.clearUser();
  },
  computed: {
    ...mapState('account', { userFromAccountModule: 'user' }),
    ...mapState('users', ['user']),
    ...mapState('roles', ['appRoles']),
    isUserMain() {
      return this.userFromAccountModule?.role.name === 'main_user';
    }
  },
  methods: {
    ...mapActions('roles', ['getAppRoles']),
    ...mapActions('users', [
      'getUser',
      'clearUser',
      'updateUser',
      'deleteUser'
    ]),
    async fetchUserAndRoles() {
      this.loading = true;

      await this.getAppRoles();
      await this.fetchUser();
      await this.initUserRole();
      this.loading = false;
    },
    async fetchUser() {
      const { id } = this.$route.params;

      await this.getUser(id);
      this.formData = {
        ...this.user
      };
    },
    initUserRole() {
      const roles: Record<string, unknown> = {
        main_user: this.$t('mainUser'),
        user: this.$t('user'),
        janitor: this.$t('janitor')
      };

      this.userRoleDropdown.options = this.appRoles.map((i: AppRole) => ({
        value: i.value,
        label: roles[i.value]
      }));

      if (this.user.pharmacyAccesses) {
        const roleNameForPharmacyId: PharmacyAccesses | undefined =
          this.user.pharmacyAccesses.find(
            (itemData: PharmacyAccesses) =>
              itemData.pharmacyId === this.pharmacyId
          );

        if (roleNameForPharmacyId) {
          this.userRoleDropdown.selectedOption =
            this.userRoleDropdown.options.find(
              (i: Option) => i.value === roleNameForPharmacyId.roleName
            );
        }
      } else {
        this.userRoleDropdown.selectedOption =
          this.userRoleDropdown.options.find(
            (i: Option) => i.value === this.user.role.name
          );
      }
    },
    selectUserRole(option: Option) {
      this.userRoleDropdown.selectedOption = option;

      if (
        this.user.pharmacyId === this.pharmacyId ||
        !this.formData.pharmacyAccesses
      ) {
        this.formData.role.name = option.value;
      }

      if (this.formData.pharmacyAccesses) {
        this.formData.pharmacyAccesses = this.formData.pharmacyAccesses.map(
          (pharmacyAccess: PharmacyAccessesAdd) => {
            if (pharmacyAccess.pharmacyId === this.pharmacyId) {
              return {
                ...pharmacyAccess,
                roleName: option.value
              };
            }

            return pharmacyAccess;
          }
        );
      }
    },
    changeCheckboxes(name: string) {
      this.formData.role[name] = !this.formData.role[name];

      if (
        this.user.pharmacyId === this.pharmacyId ||
        !this.formData.pharmacyAccesses
      ) {
        return this.formData.role[name];
      }

      if (this.formData.pharmacyAccesses) {
        this.formData.pharmacyAccesses = this.formData.pharmacyAccesses.map(
          (pharmacyAccess: PharmacyAccessesAdd) => {
            if (pharmacyAccess.pharmacyId === this.pharmacyId) {
              return {
                ...pharmacyAccess,
                [name]: this.formData.role[name]
              };
            }

            return pharmacyAccess;
          }
        );
      }
    },
    // eslint-disable-next-line
    async submitHandler(data: any) {
      const { id } = this.$route.params;
      this.loading = true;

      if (data.pharmacyAccesses) {
        data.pharmacyAccesses = data.pharmacyAccesses.map(
          (pharmacyAccess: PharmacyAccessesAdd) => {
            if (pharmacyAccess.pharmacyId === this.pharmacyId) {
              return {
                ...pharmacyAccess,
                boxAccess: data.role.boxAccess,
                webToolAccess: data.role.webToolAccess
              };
            }

            return pharmacyAccess;
          }
        );
      }

      const payload = {
        ...data,
        role: this.formData.role.name,
        boxAccess: data.role.boxAccess,
        webToolAccess: data.role.webToolAccess
      };

      delete payload.appLanguage;
      delete payload.emailSearch;
      delete payload.firstNameSearch;
      delete payload.id;
      delete payload.lastNameSearch;

      if (data.pharmacyAccesses) {
        if (this.user.pharmacyId !== this.pharmacyId) {
          delete payload.boxAccess;
          delete payload.webToolAccess;
        }
      }

      await this.updateUser({
        data: payload,
        userId: id
      });

      router.push({ name: 'Users' });
      this.loading = false;
    },
    async deleteUserRequest() {
      this.loading = true;
      const { id } = this.$route.params;

      await this.deleteUser(id);
      this.navigateBack();

      this.loading = false;
    },
    cleanForm() {
      this.$formkit.reset('addUserForm');
    },
    submitForm() {
      this.$formkit.submit('addUserForm');
    },
    dateTime(value: string) {
      return moment(value).format('DD.MM.YYYY HH:mm:ss');
    },
    navigateBack() {
      router.push({ name: 'Users' });
    }
  }
})
export default class InfoUserComponent extends Vue {}
</script>

<style lang="scss" scoped>
.infoUser {
  height: calc(100vh - 80px);
  padding: 20px;
  overflow-y: scroll;
  overflow-x: hidden;
  .infoUser__header {
    display: flex;
    justify-content: space-between;
    align-content: center;
    margin-bottom: 27px;
    .infoUser__header-changePassword {
      display: flex;
      align-items: center;
      margin-right: 30px;
      cursor: pointer;
      .title {
        margin-left: 9px;
        color: $main-green;
        font-size: $fourteenSize;
        line-height: $fourteenSize;
        font-weight: 400;
      }
    }
  }
  .infoUser__card {
    width: 100%;
    .infoUser__card-checkbox {
      margin-top: 0;
      &:last-child {
        margin-top: 24px;
      }
    }
    .infoUser__card__lastUpdate-key {
      margin-top: 32px;
      .content {
        &:last-child {
          margin-top: 32px;
          margin-bottom: 32px;
        }
        .title {
          font-size: $fourteenSize;
          line-height: 14px;
          color: $label;
          padding-bottom: 12px;
        }
        .subtitle {
          font-family: 'Ubuntu-Medium';
          font-size: $sixteenSize;
          line-height: 14px;
        }
      }
    }
  }
  .infoUser__changePassword {
    display: flex;
    align-items: center;
    margin-right: 30px;
    margin-bottom: 24px;
    cursor: pointer;
    .title {
      margin-left: 9px;
      color: $main-green;
      font-size: $fourteenSize;
    }
  }
}
</style>
